import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { HttpHeaders } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { EventTypes } from '@models/server-common';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { AuthenticatedUser, TokenUser } from '@quorum/authentication/state';
import { CommunicatorFacade } from '@quorum/communicator-ngrx';
import { GmMyRewardsOffersComponent, GmMyRewardsService } from '@quorum/gm-my-rewards';
import {
  GmAccountInformationQueryParameters,
  GmDealerEligibilityQueryParameters,
  GmMemberLookupQueryParameters,
  GmSoftRegisterEmailQueryParameters,
} from '@quorum/models/oem-query';
import {
  ServiceAppointmentQueryParameters as AppointmentQueryParameters,
  AssociateQueryParameters,
  ConversationQueryParameters,
} from '@quorum/models/xs-query';
import {
  Address,
  AlternateTransportation,
  ServiceAppointment as Appointment,
  Associate,
  AssociateReport,
  Conversation,
  DirectMessageTemplate,
  Employee,
  Event,
  Member,
  MessageTransactionMessagePreference,
  OemRooftopLink,
  Template,
  Vehicle,
  VehicleBody,
} from '@quorum/models/xs-resource';
import { OemMenuService } from '@quorum/quorum-api';
import { ServiceLaneDropdownsFacade } from '@quorum/service-lane-dropdowns/facade';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { TextMessageDialogComponent } from '@quorum/sha-components-ng-ui';
import { MediaStateService } from '@quorum/sha-media';
import { SvcVehicleDiagnosticsRootFacade } from '@quorum/svc-vehicle-diagnostics-root';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import { GmMyRewardsVerifyAccountDialogComponent } from 'libs/gm-my-rewards/src/lib/gm-my-rewards-verify-account-dialog/gm-my-rewards-verify-account-dialog.component';
import { createFormGroupFromObject } from 'libs/sha-services/src/lib/createFormGroupFromObject';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { AppointmentConfirmSendConfirmationDialogComponent } from '../appointment-confirm-send-confirmation-dialog/appointment-confirm-send-confirmation-dialog.component';
// eslint-disable-next-line max-len
import { InspectionService } from '@quorum/xsr-legacy-vip/services';
import { MediaGalleryDialogComponent } from 'libs/sha-components-ng-ui/src/lib/media-gallery-dialog/media-gallery-dialog.component';
import type { Attachment } from 'libs/xsr-template-editor-ng-ui/src/lib/communicator-dialog-editor/communicator-dialog-editor.component';
import { CustomerNotesDialogComponent } from '../customer-notes-dialog/customer-notes-dialog.component';
import { EditAppointmentDialogComponent } from '../edit-appointment-dialog/edit-appointment-dialog.component';
import { EditCustomerDialogComponent } from '../edit-customer-dialog/edit-customer-dialog.component';
import { EditVehicleDialogComponent } from '../edit-vehicle-dialog/edit-vehicle-dialog.component';
import { environment } from '@quorum/environments';

const SERVICE_CODE_REQUIRED_CONTROL = 'CL_SERVICE_CODE_REQUIRED';
const SERVICE_CODE_REQUIRED_HARD_EDIT = 'H';

@Component({
  selector: 'svc-appointment-summary',
  templateUrl: './appointment-summary.component.html',
  styleUrls: ['./appointment-summary.component.scss'],
})
export class AppointmentSummaryComponent implements OnInit, OnDestroy {
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;
  @Input() appointment$: Observable<Appointment>;
  @Input() vehicle$: Observable<Vehicle>;
  @Input() associate$: Observable<Associate>;
  @Input() address$: Observable<Address>;
  @Input() appointmentDropdowns$: Observable<any>;
  @Input() vehicleDropdowns$: Observable<any>;
  @Input() toggleTireFinderWidgetValue: boolean;
  @Output() editModeOutput: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() toggleTireFinder: EventEmitter<boolean> = new EventEmitter<boolean>(false);

  vehicleForm: FormGroup;
  associateForm: FormGroup;
  addressForm: FormGroup;
  systemControl$: Observable<any>;
  oem: string;
  currentEmployee: Employee;
  authenticatedUser$: Observable<AuthenticatedUser>;
  authenticatedEmployee$: Observable<Employee>;
  cadillacConcierge = false;
  locations$: any;
  loading: any;
  vehicleMaintenanceSchedule$: any;
  vehicleMaintenanceSchedules: any[] = [];
  workOrderId: number;
  directMessageTemplates$: Observable<DirectMessageTemplate[]>;
  inspectionUrl$: Subject<string> = new Subject<string>();
  customerEquityValue: number;
  //refreshDashboardData = false;
  appointmentFormInvalid: boolean;
  searchedAssociateName = '';
  filteredAssociates$: BehaviorSubject<AssociateReport[]> = new BehaviorSubject<AssociateReport[]>([]);

  bodyDropdowns$: Observable<VehicleBody[]>;
  showAddressForm = false;
  showAssociateForm = false;
  associateFormCombinedSub: Subscription;
  resourceUpdateCheck: { appointment: boolean; vehicle: boolean; associate: boolean; address: boolean } = {
    appointment: false,
    vehicle: false,
    associate: false,
    address: false,
  };
  vehicleFormSubscription: Subscription;
  appointmentFormSubscription: Subscription;
  associateFormSubscription: Subscription;
  addressFormSubscription: Subscription;
  vehicleFormNameSub: Subscription;
  serviceAppointmentConfirmationSent = false;
  isServiceCodeRequiredHardEdit = false;
  listenForCreateConversationSuccessSub: Subscription;
  mediaParams$: Observable<any>;

  gmMyRewardsDataLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isGmMyRewardsCustomerInformationDialogOpen = false;
  gmMyRewardsSubject$: ReplaySubject<{ emailAddress: string }> = new ReplaySubject(1);
  gmMyRewardsCustomerInformationOverlayPositions = [
    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'center', overlayY: 'top' }),
  ];
  destroy$ = new Subject();
  environment: string = undefined;

  constructor(
    public serviceLaneDropdownFacade: ServiceLaneDropdownsFacade,
    public serviceLaneFacade: ServiceLaneFacade,
    public svcVehicleDiagnosticsRootFacade: SvcVehicleDiagnosticsRootFacade,
    public dialog: MatDialog,
    private systemControlStateService: SystemControlStateService,
    private authenticationStateService: AuthenticationStateService,
    private apiService: ApiService,
    private mediaStateService: MediaStateService,
    private sanitizer: DomSanitizer,
    private snackBar: MatSnackBar,
    private communicatorFacade: CommunicatorFacade,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public gmMyRewardsService: GmMyRewardsService,
    private oemMenuService: OemMenuService,
    public inspectionService: InspectionService
  ) {
    this.environment = environment.environment;
  }

  showTireFinderWidget(vehicle: Vehicle, dealerBac: string, workOrderId: string, appointmentId: string) {
    this.router.navigate(['/tire-finder'], {
      queryParams: {
        dealerCode: dealerBac,
        vin: vehicle.vin,
        workOrderId: workOrderId,
        vehicleId: vehicle.id,
        appointmentId,
      },
    });
  }

  toggleTireFinderWidget(toggleTireFinderWidgetValue: boolean) {
    this.toggleTireFinderWidgetValue = !toggleTireFinderWidgetValue;
    this.toggleTireFinder.emit(!toggleTireFinderWidgetValue);
  }

  ngOnInit() {
    this.mediaParams$ = this.mediaStateService.getMediaParamsForKey('appointmentCase');
    this.vehicle$
      .pipe(
        filter((vehicle) => vehicle != null),
        take(1)
      )
      .subscribe((vehicle) => {
        this.apiService
          .get(`v/1/reporting/crm/available`, { params: { vehicleId: vehicle.id } })
          .pipe(map((vehicleReport) => (vehicleReport.length == 1 ? vehicleReport[0] : null)))
          .subscribe((equityData) => {
            if (equityData != null && equityData.equityValue > 0) {
              this.customerEquityValue = equityData.equityValue;
            }
          });
      });

    forkJoin([
      this.associate$.pipe(
        filter((associate) => associate != null),
        take(1)
      ),
      this.appointment$.pipe(
        filter((appointment) => appointment != null),
        take(1)
      ),
      this.vehicle$.pipe(
        filter((vehicle) => vehicle != null),
        take(1)
      ),
    ]).subscribe(([associate, appointment, vehicle]) => {
      this.serviceLaneFacade.getAllNotes(associate.id, appointment.id.toString(), vehicle.id.toString());
    });

    this.serviceLaneFacade.listenForToasts().subscribe((toastOptions) => {
      this.snackBar.open(toastOptions.message, toastOptions.actionText, toastOptions.options);
    });

    this.listenForCreateConversationSuccessSub = this.serviceLaneFacade.listenForCreateConversationSuccess$
      .pipe(
        map((action) => {
          if (action.conversation.dmsMessageTypeId === 5 && action.conversation.dmsTransactionTypeId === 6) {
            this.serviceAppointmentConfirmationSent = true;
          }
        })
      )
      .subscribe();

    this.authenticatedUser$ = this.authenticationStateService.selectAuthenticatedUser().pipe(
      filter((authenticatedUser) => authenticatedUser != null),
      take(1)
    );

    this.authenticatedEmployee$ = this.authenticationStateService.selectAuthenticatedEmployee().pipe(
      filter((authenticatedEmployee) => authenticatedEmployee != null),
      take(1)
    );

    this.directMessageTemplates$ = this.serviceLaneDropdownFacade.directMessageTemplatesDropdown$;

    this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(
        filter((currentUser) => currentUser != null),
        takeUntil(this.destroy$),
        map((currentUser) => {
          this.currentEmployee = currentUser;
          return currentUser.rooftopId;
        })
      )
      .subscribe((rooftopId) => {
        combineLatest([
          this.determineOEM(rooftopId),
          this.appointment$.pipe(filter((appointment) => appointment != null)),
          this.authenticationStateService
            .selectAuthenticatedUser()
            .pipe(filter((authenticatedUser) => authenticatedUser != null)),
          this.systemControlStateService.getSystemControlValues([SERVICE_CODE_REQUIRED_CONTROL], rooftopId),
        ])
          .pipe(takeUntil(this.destroy$))
          .subscribe(([oem, appointment, authenticatedUser, serviceCodeSystemControls]) => {
            this.isServiceCodeRequiredHardEdit =
              serviceCodeSystemControls?.[SERVICE_CODE_REQUIRED_CONTROL]?.value === SERVICE_CODE_REQUIRED_HARD_EDIT;
            this.oem = oem;
            if (oem === 'GM') {
              this.gmMyRewardsSubject$.next(null);
            }
            this.systemControl$ = this.systemControlStateService.getSystemControlValues(
              [
                //'CL_GM_DASHBOARD_INTEGRATION_ENABLED', --- Currently breaks any non GM rooftop.
                'CL_ENABLE_MAINTENANCE_MENU',
                'CL_COMMUNICATOR_ENABLE',
                'CL_CURRENCY',
                'CL_SHOP_SUPPLIES_PERCENT',
                'CL_UNIT_OF_MEASURE',
                'CL_ELECTRONIC_WORK_ORDER_FLAG',
                'CL_CITY',
                'CL_WORK_RECON_LINK_REF',
                'CL_GLOBAL_WARRANTY',
                'CL_WO_AUTO_TAG',
                'CL_TAG_NUM_REQUIRED',
                'CL_CUSTOMER_USING_SOFTWARE',
                'CL_OEM_VEHICLE_DIAGNOSTIC_DATA_ENABLED',
                'CL_GM_MY_REWARDS_ENABLED',
                'CL_GM_TIRE_CENTRAL_WIDGET',
                'CL_POWER_LANE_MEDIA_RECORDER',
              ],
              rooftopId
            );

            const conversationQueryParameters: ConversationQueryParameters = new ConversationQueryParameters({
              transactionTypeId: 6,
              messageTypeId: 5,
              transactionId: appointment.id,
            });

            this.apiService
              .get<Conversation[]>('communicator/conversations', {
                params: conversationQueryParameters,
              })
              .pipe(
                map((data) => {
                  if (data[0]) {
                    this.serviceAppointmentConfirmationSent = true;
                  }
                })
              )
              .subscribe();
            this.setWorkOrderRequiredFieldsPresent({
              odometerIn: appointment.odometerIn ? true : false,
              serviceAdvisorId: appointment.serviceAdvisorId ? true : false,
              serviceCode: this.isServiceCodeRequirementSatisfied(appointment.serviceCode),
            });
          });
      });

    this.locations$ = new BehaviorSubject<any>(null).pipe(
      switchMap((provStateId: string) => {
        if (provStateId) {
          return this.serviceLaneDropdownFacade.locationsDropdowns$.pipe(
            map((locations) => locations.filter((locations) => locations.provStateId === provStateId))
          );
        } else {
          return of([]);
        }
      }),
      shareReplay(1)
    );

    this.vehicleMaintenanceSchedule$ = this.vehicle$.pipe(
      filter((vehicle) => vehicle !== null),
      switchMap((vehicle) => {
        return this.apiService
          .get(`/v/1/vehicles/vehicle-maintenance-schedules`, {
            params: { vehicleId: vehicle.id },
          })
          .pipe(
            filter((vehicleMaintenanceSchedules) => vehicleMaintenanceSchedules != null),
            tap((vehicleMaintenanceSchedules) => (this.vehicleMaintenanceSchedules = vehicleMaintenanceSchedules))
          );
      })
    );

    this.gmMyRewardsService.gmMyRewardsData$ = this.gmMyRewardsSubject$.pipe(
      switchMap((data: { emailAddress: string }) => {
        this.gmMyRewardsDataLoaded$.next(false);
        return combineLatest([
          this.authenticationStateService.selectAuthenticatedEmployee().pipe(
            filter((currentUser) => currentUser != null),
            take(1),
            switchMap((employee) => {
              return this.systemControlStateService
                .getSystemControlValues(['CL_GM_MY_REWARDS_ENABLED', 'CL_DEALER_COUNTRY_ID'], employee.rooftopId)
                .pipe(take(1));
            })
          ),
          this.authenticationStateService.selectAuthenticatedUser().pipe(
            filter((authenticatedUser) => authenticatedUser !== null),
            tap((authenticatedUser) => {
              this.oemMenuService.initialize(authenticatedUser.user.destination);
            })
          ),
          this.oemMenuService.businessId$.pipe(
            filter((businessId) => businessId != null),
            take(1)
          ),
          this.serviceLaneFacade.customerAssociate$.pipe(filter((customerAssociate) => customerAssociate !== null)),
          this.gmMyRewardsService.getDealerBac(),
        ]).pipe(
          switchMap(([systemControl, selectedUser, businessId, customerAssociate, dealerBac]) => {
            if (systemControl['CL_GM_MY_REWARDS_ENABLED'] && systemControl['CL_GM_MY_REWARDS_ENABLED'].value === '1') {
              const dealerCountryCode =
                systemControl['CL_DEALER_COUNTRY_ID'].value === 'CAN'
                  ? 'CA'
                  : systemControl['CL_DEALER_COUNTRY_ID'].value === 'USA'
                  ? 'US'
                  : 'CA';

              const gmMemberLookupQueryParameters: GmMemberLookupQueryParameters = new GmMemberLookupQueryParameters({
                EmailAddress: data ? data.emailAddress : customerAssociate.emailAddress,
              });
              return this.gmMyRewardsService
                .getMemberLookUpView(businessId, dealerCountryCode, gmMemberLookupQueryParameters)
                .pipe(
                  switchMap((memberLookup) => {
                    if (memberLookup.RespCode) {
                      const gmAccountInformationQueryParameters: GmAccountInformationQueryParameters =
                        new GmAccountInformationQueryParameters({
                          'Member Number': memberLookup.MemberNumber,
                          'Light Content': 'N',
                          ShowContent: 'N',
                        });

                      const gmDealerEligibilityQueryParameters: GmDealerEligibilityQueryParameters =
                        new GmDealerEligibilityQueryParameters({
                          MemberNumber: memberLookup.MemberNumber,
                          BAC: dealerBac,
                        });

                      return combineLatest([
                        this.gmMyRewardsService.getAccountInformationView(
                          businessId,
                          dealerCountryCode,
                          gmAccountInformationQueryParameters
                        ),
                        this.gmMyRewardsService.getDealerEligibilityView(
                          businessId,
                          dealerCountryCode,
                          gmDealerEligibilityQueryParameters
                        ),
                      ]).pipe(
                        map(([accountInformation, dealerEligibility]) => {
                          this.gmMyRewardsDataLoaded$.next(true);
                          /**
                           * If 'Redeemable Balances' exists in response we need to check if it's an array and find the
                           * proper entry (Pool code = Non vehicle).
                           */
                          if (accountInformation['LOY Member']?.['Redeemable Balances']) {
                            if (Array.isArray(accountInformation['LOY Member']['Redeemable Balances'])) {
                              accountInformation['LOY Member']['Redeemable Balances'] = accountInformation[
                                'LOY Member'
                              ]['Redeemable Balances'].splice(
                                accountInformation['LOY Member']['Redeemable Balances'].findIndex((redeemableBalance) =>
                                  redeemableBalance['Pool Code'].includes('NONVEH')
                                ),
                                1
                              );
                            }
                          }

                          return {
                            memberLookup,
                            accountInformation: {
                              ...accountInformation['LOY Member'],
                            },
                            dealerEligibility,
                            businessId,
                            dealerCountryCode,
                            emailAddress: gmMemberLookupQueryParameters.EmailAddress,
                          };
                        })
                      );
                    } else {
                      this.gmMyRewardsDataLoaded$.next(true);
                      return of({ error: 'There was an error retrieving GM My Rewards information' });
                    }
                  })
                );
            } else {
              return of(null);
            }
          })
        );
      })
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.associateFormCombinedSub) {
      this.associateFormCombinedSub.unsubscribe();
    }
    if (this.vehicleFormSubscription) {
      this.vehicleFormSubscription.unsubscribe();
    }
    if (this.appointmentFormSubscription) {
      this.appointmentFormSubscription.unsubscribe();
    }
    if (this.associateFormSubscription) {
      this.associateFormSubscription.unsubscribe();
    }
    if (this.addressFormSubscription) {
      this.addressFormSubscription.unsubscribe();
    }
    if (this.listenForCreateConversationSuccessSub) {
      this.listenForCreateConversationSuccessSub.unsubscribe();
    }
  }

  openNotesDialog(associateId: string, appointmentId: string, vehicleId: string) {
    const dialogRef = this.dialog.open(CustomerNotesDialogComponent, {
      width: '500px',
      data: { associateId: associateId, appointmentId: appointmentId, vehicleId: vehicleId },
    });

    dialogRef.afterClosed().subscribe((result) => {});
  }

  enableEditMode(vehicle: Vehicle, associate: Associate, address: Address) {
    this.vehicleForm = createFormGroupFromObject({
      ...vehicle,
      deliveryDate: new Date(
        new Date(vehicle.deliveryDate).getUTCFullYear(),
        new Date(vehicle.deliveryDate).getUTCMonth(),
        new Date(vehicle.deliveryDate).getUTCDate()
      ),
    });

    this.bodyDropdowns$ = combineLatest(
      this.apiService.get<VehicleBody[]>(`v/1/vehicles/vehicle-bodies`),
      this.vehicleForm.get('body').valueChanges.pipe(
        filter((value) => value.length > 1),
        startWith(vehicle.body)
      )
    ).pipe(
      map(([bodies, value]) => {
        return bodies.filter((body: any) => body.vehicleBody.toLowerCase().includes(value?.toLowerCase()));
      })
    );

    this.vehicleForm.addControl('name', new FormControl(''));
    this.vehicleForm
      .get('name')
      .setValue(`${vehicle.embedded?.associate?.lastName}, ${vehicle.embedded?.associate?.firstName}`);
    this.vehicleFormNameSub = this.vehicleForm['controls'].name.valueChanges
      .pipe(
        filter((input: string) => {
          if (input) {
            return input.length >= 3;
          }
        }),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((value: any) => {
          const params: AssociateQueryParameters = new AssociateQueryParameters({
            search: value,
          });
          this.searchedAssociateName = value;
          return this.apiService.get<AssociateReport[]>('v/1/reporting/associates', { params: params }).pipe(
            map((associates) => {
              this.filteredAssociates$.next(associates);

              this.associateForm
                .get('firstName')
                .setValue(
                  this.vehicleForm.get('name').value.split(',')[1]
                    ? this.vehicleForm.get('name').value.split(',')[1].trim()
                    : this.vehicleForm.get('name').value
                );
              this.associateForm
                .get('lastName')
                .setValue(
                  this.vehicleForm.get('name').value.split(',')[1]
                    ? this.vehicleForm.get('name').value.split(',')[0].trim()
                    : null
                );
            }),
            catchError((error) => {
              this.vehicleForm.enable({ emitEvent: false });
              return of(error);
            })
          );
        })
      )
      .subscribe();

    this.setVehicleFormValidation();

    this.associateForm = createFormGroupFromObject(associate);
    this.setAssociateFormValidation();
    this.addressForm = createFormGroupFromObject(address);
    this.locations$.next(address.provStateId);
    this.addressForm.get('provStateId').valueChanges.subscribe((provState: any) => {
      this.locations$.next(provState);
      this.addressForm.get('locationId').patchValue(null);
      this.addressForm.get('locationId').setValidators(Validators.required);
      this.addressForm.updateValueAndValidity();
      this.addressForm.markAllAsTouched();
    });

    this.vehicleFormSubscription = this.vehicleForm.valueChanges.subscribe((val) => {
      this.resourceUpdateCheck.vehicle = true;
    });
    this.associateFormSubscription = this.associateForm.valueChanges.subscribe((val) => {
      this.resourceUpdateCheck.associate = true;
    });
    this.addressFormSubscription = this.addressForm.valueChanges.subscribe((val) => {
      this.resourceUpdateCheck.address = true;
    });
  }

  // appointmentFormEmit(appointmentForm: FormGroup) {
  //   this.appointmentForm = appointmentForm;
  //   this.appointmentFormInvalid = appointmentForm.invalid;
  // }

  updateFormResources(
    vehicle: Vehicle,
    associate: Associate,
    address: Address,
    appointment: any,
    //refreshDashboardData: boolean,
    resourceUpdateCheck: { appointment: boolean; vehicle: boolean; associate: boolean; address: boolean },
    initialAppointment: Appointment
  ) {
    //this.showAddressForm = false;
    //this.showAssociateForm = false;

    // if (resourceUpdateCheck.appointment) {
    //   const time: Date = new Date(appointment.dateTime);
    //   time.setHours(appointment.appointmentTime.split(':')[0], appointment.appointmentTime.split(':')[1]);
    //   appointment = {
    //     ...appointment,
    //     promisedDateTime: appointment.promisedTime ? appointment.promisedDateTime : null,
    //     customerName: associate.firstName ? `${associate.lastName}, ${associate.firstName}` : `${associate.fullName}`,
    //     customerId: associate.id,
    //     odometerIn:
    //       appointment.odometerIn === null ||
    //       (appointment.odometerIn !== null && appointment.odometerIn.toString().length == 0)
    //         ? null
    //         : appointment.odometerIn,
    //     dateTime: time,
    //     cadillacConciergeDropoff: appointment.cadillacConciergeUsePickupLocation
    //       ? appointment.cadillacConciergePickup
    //       : appointment.cadillacConciergeDropoff,
    //     cadillacConciergeContactPhone:
    //       appointment.cadillacConciergeContactPhone != null
    //         ? appointment.cadillacConciergeContactPhone.replace(/\D+/g, '')
    //         : null,
    //     odometerUpdatedByVehicleDiagnostic: initialAppointment.odometerIn !== appointment.odometerIn ? false : true,
    //   };
    //   this.serviceLaneFacade.updateAppointmentOnServer(
    //     appointment,
    //     new AppointmentQueryParameters({
    //       embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption',
    //     })
    //   );
    // }

    // this.setWorkOrderRequiredFieldsPresent({
    //   odometerIn: appointment.odometerIn ? true : false,
    //   serviceAdvisorId: appointment.serviceAdvisorId ? true : false,
    // });

    // if (refreshDashboardData) {
    //   this.serviceLaneFacade.dashboardSubject$.next({
    //     params: {
    //       oemrtId: vehicle.rooftopId,
    //       vehicleVin: vehicle.vin,
    //       odometerIn: appointment.odometerIn,
    //       appointmentId: appointment.id,
    //       vehicleId: vehicle.id,
    //       vehicleYear: vehicle.year,
    //     },
    //     skipCache: false,
    //   });
    // }

    if (resourceUpdateCheck.associate) {
      this.serviceLaneFacade.updateAssociateOnServer(associate);
    }

    if (resourceUpdateCheck.address) {
      address = {
        ...address,
        cellPhoneNumber: address.cellPhoneNumber ? address.cellPhoneNumber.replace(/\D+/g, '') : null,
        homePhoneNumber: address.homePhoneNumber ? address.homePhoneNumber.replace(/\D+/g, '') : null,
      };
      this.serviceLaneFacade.updateAddressOnServer(address);
    }

    if (resourceUpdateCheck.vehicle) {
      this.serviceLaneFacade.updateVehicleOnServer(vehicle);
    }

    this.resourceUpdateCheck.appointment = false;
    this.resourceUpdateCheck.associate = false;
    this.resourceUpdateCheck.address = false;
    this.resourceUpdateCheck.vehicle = false;
  }

  // verifyCustomer(appointment: Appointment) {
  //   const dialogRef = this.dialog.open(AppointmentConfirmationDialogComponent, {
  //     width: '800px',
  //     data: { associateId: appointment.customerId },
  //   });

  //   dialogRef.afterClosed().subscribe((result) => {
  //     if (result == 'confirmed') {
  //       const updatedAppointment = new Appointment({ ...appointment });
  //       updatedAppointment.customerVerified = true;
  //       this.serviceLaneFacade.updateAppointmentOnServer(updatedAppointment, {});
  //     }
  //   });
  // }

  setVehicleFormValidation() {
    this.vehicleForm.get('name').setValidators(Validators.required);
    this.vehicleForm
      .get('year')
      .setValidators([Validators.pattern('^[0-9]+$'), Validators.minLength(4), Validators.maxLength(4)]);
  }

  setAssociateFormValidation() {
    this.associateForm.get('emailAddress').setValidators([Validators.email]);
  }

  sendAppointmentConfirmation(
    associate: Associate,
    authenticatedUser: TokenUser,
    systemControls: any,
    appointment: Appointment,
    address: Address
  ) {
    const dialogRef = this.dialog.open(AppointmentConfirmSendConfirmationDialogComponent, {
      width: '570px',
      height: '200px',
      data: {
        messagePreferenceId: associate.messagePreferenceId,
        emailAddress: associate.emailAddress,
        phoneNumber: address.cellPhoneNumber,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'confirmed' && !appointment.communicatorExcludeFrom) {
        this.serviceLaneFacade.sendServiceAppointmentConfirmation(
          appointment,
          systemControls,
          authenticatedUser,
          associate
        );

        let toastOptions: any = {
          message: `Communicator message sent.`,
          actionText: null,
          options: { duration: 3000 },
        };

        this.serviceLaneFacade.addToast(toastOptions);
      }
    });
  }

  openCommunicatorTextDialog(
    directMessageTemplates: DirectMessageTemplate[],
    associate: Associate,
    currentUser: TokenUser,
    appointment: Appointment
  ) {
    let conversationQueryParameters: ConversationQueryParameters = new ConversationQueryParameters({
      transactionTypeId: 6,
      messageTypeId: 18,
      transactionId: appointment.id,
    });

    let event: Event = new Event({
      conversationId: null,
      memberId: currentUser.id.toString(),
      eventTypeId: EventTypes.Message,
      content: 'Generating message...',
      guid: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
      sentDate: new Date(),
      sent: false,
      sendToCustomer: true,
      id: Math.floor(Math.random() * (9999999 - 999999)) + 999999,
    });

    let conversation: Conversation = new Conversation({
      dmsMessageTypeId: 18,
      dmsTransactionTypeId: 6,
      dmsTransactionId: appointment.id,
      createdBy: currentUser.id,
    });

    let members: Member[] = [];
    members.push(
      new Member({
        userId: currentUser.id.toString(),
        avatarUrl: currentUser.avatarUrl,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        nickname: currentUser.firstName,
        isEmployee: true,
        isRead: true,
        isArchived: false,
        isExcluded: false,
        isPinnedToUi: false,
      })
    );

    members.push(
      new Member({
        userId: associate.id,
        firstName: associate.firstName,
        lastName: associate.lastName,
        nickname: associate.firstName,
        isEmployee: false,
        isRead: true,
        isArchived: false,
        isExcluded: false,
        isPinnedToUi: false,
      })
    );

    let toastOptions: any = {
      message: `Communicator message sent.`,
      actionText: null,
      options: { duration: 3000 },
    };

    this.apiService.get(`/quorum/stores/${currentUser.storeId}`).subscribe((response) => {
      // QTemplates enabled
      if (response.useCommunicatorCloudServices) {
        this.apiService
          .get(`v/1/quorum/templates/store-gallery/${currentUser.destination}`, {
            params: { messageTypeId: 18 },
          })
          .subscribe(async (communicatorTemplates) => {
            try {
              const { CommunicatorDialogEditorComponent } = await import('@quorum/xsr-template-editor-ng-ui');
              const dialogRef = this.dialog.open(CommunicatorDialogEditorComponent, {
                data: {
                  currentUser: currentUser,
                  messageType: 'text',
                  templates: communicatorTemplates,
                },
                height: '100%',
                width: '100%',
              });

              dialogRef.afterClosed().subscribe((dialogResult) => {
                if (dialogResult && dialogResult.template) {
                  this.apiService
                    .post(`v/1/quorum/templates/item-specific/${currentUser.destination}`, dialogResult.template)
                    .subscribe((itemSpecificTemplate: Template) => {
                      event = { ...event, content: 'Generating message...' };
                      let optionalHeader = new HttpHeaders({ 'template-id': itemSpecificTemplate.id });
                      this.communicatorFacade.sendCommunicatorMessage(
                        conversationQueryParameters,
                        conversation,
                        members,
                        event,
                        optionalHeader
                      );

                      this.serviceLaneFacade.addToast(toastOptions);
                    });
                } else if (dialogResult.messageContent) {
                  event = { ...event, content: dialogResult.messageContent };
                  let optionalHeader = new HttpHeaders({ 'Use-Default-Template': 'false' });
                  this.communicatorFacade.sendCommunicatorMessage(
                    conversationQueryParameters,
                    conversation,
                    members,
                    event,
                    optionalHeader
                  );

                  this.serviceLaneFacade.addToast(toastOptions);
                }
              });
            } catch (err) {
              console.error('Failed to load communicator dialog module', err);
              this.snackBar.open(
                'Could not load the communicator. Please check your connection and try again.',
                'Dismiss',
                { duration: 5000 }
              );
            }
          });
      } else {
        const dialogRef = this.dialog.open(TextMessageDialogComponent, {
          width: '500px',
          height: '350px',
          data: {
            directMessageTemplates: directMessageTemplates,
          },
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            event = { ...event, content: result.textMessageBody };
            let optionalHeader = new HttpHeaders({ 'Use-Default-Template': 'false' });

            this.communicatorFacade.sendCommunicatorMessage(
              conversationQueryParameters,
              conversation,
              members,
              event,
              optionalHeader
            );

            this.serviceLaneFacade.addToast(toastOptions);
          }
        });
      }
    });
  }

  openCommunicatorEmailDialog(currentUser: TokenUser, appointment: Appointment, associate: Associate) {
    this.apiService.get(`/quorum/stores/${currentUser.storeId}`).subscribe((response) => {
      if (response.useCommunicatorCloudServices) {
        this.apiService
          .get(`v/1/quorum/templates/store-gallery/${currentUser.destination}`, {
            params: { messageTypeId: 18 },
          })
          .subscribe(async (templates) => {
            try {
              const { CommunicatorDialogEditorComponent } = await import('@quorum/xsr-template-editor-ng-ui');
              const dialogRef = this.dialog.open(CommunicatorDialogEditorComponent, {
                data: { currentUser: currentUser, messageType: 'email', templates: templates },
                height: '100%',
                width: '100%',
              });

              dialogRef.afterClosed().subscribe((dialogResult) => {
                if (dialogResult) {
                  this.apiService
                    .post(`v/1/quorum/templates/item-specific/${currentUser.destination}`, dialogResult.template)
                    .subscribe((itemSpecificTemplate: Template) => {
                      let conversationQueryParameters: ConversationQueryParameters = new ConversationQueryParameters({
                        transactionTypeId: 10,
                        messageTypeId: 18,
                        transactionId: associate.id,
                      });

                      let event: Event = new Event({
                        attachments: dialogResult.attachments
                          ? dialogResult.attachments.map((a: Attachment) => a.url).join(',')
                          : null,
                        conversationId: null,
                        memberId: currentUser.id.toString(),
                        eventTypeId: EventTypes.Message,
                        content: 'Generating email...',
                        guid: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
                        sentDate: new Date(),
                        sent: false,
                        sendToCustomer: true,
                        id: Math.floor(Math.random() * (9999999 - 999999)) + 999999,
                      });

                      let conversation: Conversation = new Conversation({
                        dmsMessageTypeId: 18,
                        dmsTransactionTypeId: 10,
                        dmsTransactionId: associate.id,
                        createdBy: currentUser.id,
                      });

                      let members: Member[] = [];
                      members.push(
                        new Member({
                          userId: currentUser.id.toString(),
                          avatarUrl: currentUser.avatarUrl,
                          firstName: currentUser.firstName,
                          lastName: currentUser.lastName,
                          nickname: currentUser.firstName,
                          isEmployee: true,
                          isRead: true,
                          isArchived: false,
                          isExcluded: false,
                          isPinnedToUi: false,
                        })
                      );

                      members.push(
                        new Member({
                          userId: associate.id,
                          firstName: associate.firstName,
                          lastName: associate.lastName,
                          nickname: associate.firstName,
                          isEmployee: false,
                          isRead: true,
                          isArchived: false,
                          isExcluded: false,
                          isPinnedToUi: false,
                        })
                      );

                      let optionalHeader = new HttpHeaders({ 'template-id': itemSpecificTemplate.id });

                      this.communicatorFacade.sendCommunicatorMessage(
                        conversationQueryParameters,
                        conversation,
                        members,
                        event,
                        optionalHeader
                      );

                      let toastOptions: any = {
                        message: `Communicator email sent.`,
                        actionText: null,
                        options: { duration: 3000 },
                      };

                      this.serviceLaneFacade.addToast(toastOptions);
                    });
                }
              });
            } catch (err) {
              console.error('Failed to load communicator dialog module', err);
              this.snackBar.open(
                'Could not load the communicator. Please check your connection and try again.',
                'Dismiss',
                { duration: 5000 }
              );
            }
          });
      }
    });
  }

  // serviceCodeSelected(serviceCode: string, serviceCodes: ServiceCode[]) {
  //   let sc = serviceCodes.find((s) => s.serviceCode == serviceCode);
  //   let timeDate = null;
  //   if (sc.dateTimePromised != null) {
  //     timeDate = moment(sc.dateTimePromised.split('T')[1].substr(0, 5), 'hh:mm')
  //       .subtract(new Date().getTimezoneOffset(), 'minutes')
  //       .format('HH:mm');
  //   }

  //   this.appointmentForm.patchValue({ priorityNumber: sc.priorityNumber, promisedTime: timeDate });
  // }

  // techOrGroupSelected(event: any, technicians: Employee[], techGroups: TechnicianGroup[]) {
  //   if (technicians.findIndex((t) => t.associateId == event.value) > -1) {
  //     let tech: Employee = technicians.find((t) => t.associateId == event.value);
  //     this.appointmentForm.get('technicianId').setValue(tech.associateId);
  //     this.appointmentForm
  //       .get('technicianGroupName')
  //       .setValue(`${tech.embedded.associate.firstName} ${tech.embedded.associate.lastName} `);
  //   } else {
  //     let group: TechnicianGroup = techGroups.find((g) => g.id == event.value);
  //     this.appointmentForm.get('technicianGroupId').setValue(group.id);
  //     this.appointmentForm.get('technicianGroupName').setValue(group.description);
  //   }
  // }

  // transportOptionSelected(event: any, transportOptions: TransportOption[]) {
  //   let selectedTransportOption = transportOptions.find((transportOption) => transportOption.id === event);
  //   if (selectedTransportOption.name.toLowerCase() === 'cadillac premium concierge') {
  //     this.cadillacConcierge = true;
  //     this.appointmentForm.get('cadillacConciergeContactPhone').setValidators(Validators.required);
  //     this.appointmentForm.get('cadillacConciergeContactPhone').updateValueAndValidity();
  //   } else {
  //     this.cadillacConcierge = false;
  //     this.appointmentForm.patchValue({
  //       cadillacConciergeContactPhone: null,
  //       cadillacConciergeDropoff: null,
  //       cadillacConciergePickup: null,
  //     });
  //   }
  // }

  // cancelEdit() {
  //   this.resourceUpdateCheck.appointment = false;
  //   this.resourceUpdateCheck.associate = false;
  //   this.resourceUpdateCheck.address = false;
  //   this.resourceUpdateCheck.vehicle = false;
  //   this.showAddressForm = false; //new
  //   this.showAssociateForm = false; //new
  // }

  setWorkOrderRequiredFieldsPresent(workOrderRequiredFieldsPresent: {
    odometerIn: boolean;
    serviceAdvisorId: boolean;
    serviceCode: boolean;
  }) {
    this.serviceLaneFacade.setWorkOrderRequiredFieldsPresentInState(workOrderRequiredFieldsPresent);
  }

  isServiceCodeRequirementSatisfied(serviceCode: string | null): boolean {
    if (!this.isServiceCodeRequiredHardEdit) {
      return true;
    }
    return typeof serviceCode === 'string' && serviceCode.trim().length > 0;
  }

  closeGmMyRewardsDialog() {
    this.isGmMyRewardsCustomerInformationDialogOpen = false;
  }

  // function that mimics opening the new vehicle diagnostic shared component from within XS,
  // to be used when developing/testing the shared component
  openVehicleDiagnosticsFromXS(oem: string) {
    this.router.navigate([`oem-vehicle-diagnostics`], {
      relativeTo: this.activatedRoute.parent,
      queryParams: {
        oem: oem,
        transactionId: '76087', // id of the wo/sa/sq 76082 - Posted 76087 - Not posted
        transactionType: 'workOrder', //workOrder, service appointment, or service quote?
        serviceVisitId: '0ca785ed6b9cf25630963da5dfc5a4e6',
        vin: '1GKS2BKD1MR196583',
        dealerCountryCode: 'CA',
      },
    });
  }

  openInspectionFromXS() {
    this.router.navigate([`tech/inspection`], {
      relativeTo: this.activatedRoute.parent,
      queryParams: {
        workOrderId: '76185', //76146 - luckys
        //transactionId: '998877', // id of the wo/sa/sq
        //transactionType: 'workOrder', //workOrder, service appointment, or service quote?
        //serviceVisitId: 'b2cfb22ee0ae7f52c542c66ea0e296c5',
        //vin: '1GKS2BKD1MR196583',
        //dealerCountryCode: 'CA',
      },
    });
  }
  openMenuFromXS(oem: string) {
    this.router.navigate([`oem-menu`], {
      relativeTo: this.activatedRoute.parent,
      queryParams: {
        oem: oem,
        vehicleId: '39024',
        transactionId: '84011',
        transactionType: 'serviceAppointment',
        // transactionId: '28520',
        // transactionType: 'quote',
        odometer: 75000,
      },
    });
  }

  /** GM MY REWARDS FUNCTIONS START */
  openVerifyDialog(associate: Associate) {
    const dialogRef = this.dialog.open(GmMyRewardsVerifyAccountDialogComponent, {
      data: { associate: associate, mode: 'Verify' },
      disableClose: true,
      width: '658px',
      height: '382px',
    });

    dialogRef.afterClosed().subscribe((result: { firstName: string; emailAddress: string }) => {
      if (result) {
        this.gmMyRewardsSubject$.next({
          emailAddress: result.emailAddress,
        });
      }
    });
  }

  openRegisterDialog(
    associate: Associate,
    businessId: string,
    dealerCountryCode: string,
    memberStatus: string | undefined
  ) {
    const dialogRef = this.dialog.open(GmMyRewardsVerifyAccountDialogComponent, {
      data: { associate: associate, mode: 'Register', memberStatus: memberStatus },
      disableClose: true,
      width: '658px',
      height: '507px',
    });

    dialogRef.afterClosed().subscribe((results: { firstName: string; emailAddress: string }) => {
      if (results) {
        const gmSoftRegisterEmailQueryParameters: GmSoftRegisterEmailQueryParameters =
          new GmSoftRegisterEmailQueryParameters({
            FirstName: results.firstName,
            EmailAddress: results.emailAddress,
          });
        console.log(businessId, dealerCountryCode, gmSoftRegisterEmailQueryParameters);
        this.gmMyRewardsService
          .softRegisterEmail(businessId, dealerCountryCode, gmSoftRegisterEmailQueryParameters)
          .pipe(take(1))
          .subscribe((registrationResponse) => {
            if (registrationResponse.RespCode && registrationResponse.RespCode === '200') {
              const toastOptions: any = {
                message: `GM My Rewards registration complete.`,
                actionText: null,
                options: { duration: 3000 },
              };

              this.serviceLaneFacade.addToast(toastOptions);
            }
          });
      }
    });
  }

  openOffersDialog(offers: any) {
    //XS-2931 test data until we get real data from GM
    /*const offerOne = {
      offerName: '30% off an oil change',
      offerDescription: 'This is a great offer, 30% off your next Oil Change',
    };
    const offerTwo = {
      offerName: 'Save the Tax',
      offerDescription: 'You Pay no taxes today. (could you imagine having to code this if it was real)',
    };
    const offers = [offerOne, offerTwo]*/
    this.dialog.open(GmMyRewardsOffersComponent, {
      width: '658px',
      minHeight: '307px',
      data: { offers$: offers },
    });
  }
  /** GM MY REWARDS FUNCTIONS END */

  determineOEM(rooftopId: number): Observable<string> {
    return this.apiService
      .get(`/v/1/system/oem-rooftop-links/${rooftopId}`)
      .pipe(map((oemRooftopLink: OemRooftopLink) => oemRooftopLink.associateId));
  }

  buildMediaParams(baseParams: any, additionalParams: any) {
    return { ...baseParams, ...additionalParams };
  }

  editVehicleDialog(
    vehicle: Vehicle,
    associate: Associate,
    address: Address,
    appointment: Appointment,
    vehicleDropdowns: any
  ) {
    this.enableEditMode(vehicle, associate, address);

    const dialogRef = this.dialog.open(EditVehicleDialogComponent, {
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      panelClass: 'edit-vehicle-container',
      hasBackdrop: false,
      data: {
        vehicleForm: this.vehicleForm,
        associateForm: this.associateForm,
        addressForm: this.addressForm,
        vehicleDropdowns: vehicleDropdowns,
        bodyDropdowns$: this.bodyDropdowns$,
        vehicle: vehicle,
        appointment: appointment,
        locations$: this.locations$,
        filteredAssociates$: this.filteredAssociates$,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: { vehicleForm: FormGroup; associateForm: FormGroup; addressForm: FormGroup }) => {
        if (result) {
          this.resourceUpdateCheck.vehicle ? this.vehicleForm.patchValue(result.vehicleForm) : '';
          this.resourceUpdateCheck.associate ? this.associateForm.patchValue(result.associateForm) : '';
          this.resourceUpdateCheck.address ? this.addressForm.patchValue(result.addressForm) : '';

          const updatedAppointment = new Appointment({
            ...appointment,
            odometerIn: result.vehicleForm.getRawValue().currentMileage,
          });
          this.serviceLaneFacade.updateAppointmentOnServer(
            updatedAppointment,
            new AppointmentQueryParameters({
              embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption',
            })
          );

          this.updateFormResources(
            this.vehicleForm.value,
            this.associateForm.value,
            this.addressForm.value,
            //this.appointmentForm.getRawValue(),
            appointment,
            //this.refreshDashboardData,
            this.resourceUpdateCheck,
            updatedAppointment
          );
        }
      });
  }

  editCustomerDialog(
    vehicle: Vehicle,
    associate: Associate,
    address: Address,
    appointment: Appointment,
    appointmentDropdowns: any,
    workOrderId: string
  ) {
    this.enableEditMode(vehicle, associate, address);

    const dialogRef = this.dialog.open(EditCustomerDialogComponent, {
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      panelClass: 'edit-customer-container',
      hasBackdrop: false,
      data: {
        associateForm: this.associateForm,
        addressForm: this.addressForm,
        appointmentDropdowns: appointmentDropdowns,
        locations$: this.locations$,
        appointment: appointment,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: { associateForm: FormGroup; addressForm: FormGroup; overrideOptionsForm: FormGroup }) => {
        if (result) {
          this.resourceUpdateCheck.associate ? this.associateForm.patchValue(result.associateForm) : '';
          this.resourceUpdateCheck.address ? this.addressForm.patchValue(result.addressForm) : '';

          const overrideOptions = result.overrideOptionsForm.getRawValue();
          const updatedAppointment = new Appointment({
            ...appointment,
            communicatorExcludeFrom: overrideOptions.excludeCommunicator ? true : false,
            customerVerified: true,
          });
          this.serviceLaneFacade.updateAppointmentOnServer(
            updatedAppointment,
            new AppointmentQueryParameters({
              embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption',
            })
          );

          if (overrideOptions.messagePreference != null && overrideOptions.messagePreferenceContactInfo != null) {
            this.serviceLaneFacade.createMessageTransactionMessagePreferenceOnServer(
              new MessageTransactionMessagePreference({
                ...new MessageTransactionMessagePreference(),
                messagePreferenceId: overrideOptions.messagePreference,
                messagePreferenceContactInfo: overrideOptions.messagePreferenceContactInfo,
                transactionTypeId: 2,
                transactionId: workOrderId,
              })
            );
          }

          this.updateFormResources(
            this.vehicleForm.value,
            this.associateForm.value,
            this.addressForm.value,
            //this.appointmentForm.getRawValue(),
            appointment,
            //this.refreshDashboardData,
            this.resourceUpdateCheck,
            updatedAppointment
          );
        }
      });
  }

  editAppointmentDialog(
    initialAppointment: Appointment,
    appointmentDropdowns: any,
    authenticatedEmployee: Employee,
    vehicle: Vehicle
  ) {
    const dialogRef = this.dialog.open(EditAppointmentDialogComponent, {
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      panelClass: 'edit-appointment-container',
      hasBackdrop: false,
      data: {
        appointmentDropdowns: appointmentDropdowns,
        initialAppointment: initialAppointment,
        authenticatedEmployee: authenticatedEmployee,
        vehicle: vehicle,
        isServiceCodeRequired: this.isServiceCodeRequiredHardEdit,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe(
        (result: {
          updatedAppointment: any;
          addTransportDetails: boolean;
          transportationDetailsDropOff: AlternateTransportation;
          transportationDetailsPickUp: AlternateTransportation;
          transportationDetailsDropOffExists: boolean;
          transportationDetailsPickUpExists: boolean;
        }) => {
          if (result) {
            let updatedAppointment = result.updatedAppointment;

            // console.log(new CompareObjectsPipe().transform(appointment, result.appointment));
            // if (!new CompareObjectsPipe().transform(appointment, result.appointment)) {
            //   console.log('update dat shit');
            //   this.resourceUpdateCheck.appointment = true;
            // }

            const time: Date = new Date(updatedAppointment.dateTime);
            time.setHours(
              updatedAppointment.appointmentTime.split(':')[0],
              updatedAppointment.appointmentTime.split(':')[1]
            );
            updatedAppointment = {
              ...updatedAppointment,
              promisedDateTime: updatedAppointment.promisedTime ? updatedAppointment.promisedDateTime : null,
              odometerIn:
                updatedAppointment.odometerIn === null ||
                (updatedAppointment.odometerIn !== null && updatedAppointment.odometerIn.toString().length == 0)
                  ? null
                  : updatedAppointment.odometerIn,
              dateTime: time,
              cadillacConciergeDropoff: updatedAppointment.cadillacConciergeUsePickupLocation
                ? updatedAppointment.cadillacConciergePickup
                : updatedAppointment.cadillacConciergeDropoff,
              cadillacConciergeContactPhone:
                updatedAppointment.cadillacConciergeContactPhone != null
                  ? updatedAppointment.cadillacConciergeContactPhone.replace(/\D+/g, '')
                  : null,
              odometerUpdatedByVehicleDiagnostic:
                updatedAppointment.odometerIn !== initialAppointment.odometerIn
                  ? false
                  : initialAppointment.odometerUpdatedByVehicleDiagnostic,
            };

            this.serviceLaneFacade.updateAppointmentOnServer(
              updatedAppointment,
              new AppointmentQueryParameters({
                embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption',
              })
            );

            this.setWorkOrderRequiredFieldsPresent({
              odometerIn: updatedAppointment.odometerIn ? true : false,
              serviceAdvisorId: updatedAppointment.serviceAdvisorId ? true : false,
              serviceCode: this.isServiceCodeRequirementSatisfied(updatedAppointment.serviceCode),
            });

            // If the 'Add Transportation Details' toggle is enabled
            if (result.addTransportDetails) {
              // Check to see if there are drop off details already, if so, update with latest
              if (result.transportationDetailsDropOffExists) {
                this.serviceLaneFacade.updateAlternateTransportation(result.transportationDetailsDropOff);
              } else {
                this.serviceLaneFacade.createAlternateTransportation(result.transportationDetailsDropOff);
              }

              // Check to see if there are pick up details already, if so, update with latest
              if (result.transportationDetailsPickUpExists) {
                this.serviceLaneFacade.updateAlternateTransportation(result.transportationDetailsPickUp);
              } else {
                this.serviceLaneFacade.createAlternateTransportation(result.transportationDetailsPickUp);
              }
            }
          }
        }
      );
  }

  mediaGalleryDialog(vehicleId: number) {
    this.mediaStateService.loadMediaForVehicle(vehicleId);
    const dialogData: any = {
      media$: this.mediaStateService.getVehicleMedia(),
      showFilter: true,
    };
    this.dialog.open(MediaGalleryDialogComponent, {
      width: '800px',
      height: '600px',
      data: dialogData,
      disableClose: true,
    });
  }
}
